import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CreditCard, QrCode, FileText, ShieldCheck } from "lucide-react";

// Forma de pagamento do cadastro (contratação sem trial).
// Cartão é o padrão e vai pela página segura do Asaas: o número do cartão
// nunca passa pela OPLYX. Pix e boleto liberam a conta na hora.
// Usado no fim do cadastro (AuthPage) e na tela de bloqueio (SubscriptionGate).

const TERMS_URL = "https://oplyx.com.br/termos";

const METHODS = [
  { key: "CREDIT_CARD", label: "Cartão de crédito", hint: "Cobrança automática todo mês", Icon: CreditCard },
  { key: "PIX", label: "Pix", hint: "A fatura chega por e-mail todo mês", Icon: QrCode },
  { key: "BOLETO", label: "Boleto", hint: "A fatura chega por e-mail todo mês", Icon: FileText },
];

const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const dia = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR") : "");
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const EMPTY_ADDR = { cep: "", logradouro: "", numero: "", complemento: "", bairro: "", municipio: "", uf: "" };

export default function PaymentSetup({ onActivated, initialAddress }) {
  const [info, setInfo] = useState(null);
  const [method, setMethod] = useState("CREDIT_CARD");
  const [terms, setTerms] = useState(false);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/payment-setup")
      .then((r) => {
        setInfo(r.data);
        setAddr({ ...EMPTY_ADDR, ...(initialAddress || {}), ...(r.data.billing_address || {}) });
      })
      .catch(() => setInfo({ error: true }));
    // initialAddress só preenche a primeira carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookupCep = async (cep) => {
    const d = onlyDigits(cep);
    if (d.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
      const j = await r.json();
      if (j.erro) return;
      setAddr((a) => ({
        ...a,
        logradouro: a.logradouro || j.logradouro || "",
        bairro: a.bairro || j.bairro || "",
        municipio: j.localidade || a.municipio,
        uf: j.uf || a.uf,
      }));
    } catch {
      /* o cliente preenche à mão */
    }
  };

  if (!info) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Carregando…
      </div>
    );
  }
  if (info.error) {
    return <p className="text-sm text-rose-600">Não foi possível carregar a forma de pagamento. Atualize a página.</p>;
  }
  if (info.is_owner === false) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="payment-setup-not-owner">
        Peça ao responsável pela conta para entrar e cadastrar a forma de pagamento.
      </p>
    );
  }

  const needsTerms = !info.terms_accepted;
  const addrOk = onlyDigits(addr.cep).length === 8 && addr.logradouro.trim() && addr.numero.trim() && addr.bairro.trim();
  const canSubmit = !busy && (!needsTerms || terms) && (method !== "CREDIT_CARD" || addrOk);
  const diaFixo = info.next_due ? Number(info.next_due.slice(8, 10)) : null;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { method, accepted_terms: needsTerms ? terms : true };
      if (method === "CREDIT_CARD") body.address = { ...addr, cep: onlyDigits(addr.cep) };
      const { data } = await api.post("/billing/payment-setup", body);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(`Pronto! Sua conta está liberada. A 1ª mensalidade vence em ${dia(data.first_due)}.`);
      onActivated?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && (det.message || det)) || "Não foi possível concluir. Tente de novo.");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="payment-setup">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 px-4 py-3 text-sm" data-testid="payment-setup-today">
        <b>Hoje você não paga nada.</b> A 1ª mensalidade vence em <b>{dia(info.first_due)}</b>{info.custom_first_due ? ", na data combinada com a OPLYX." : ", 5 dias depois deste cadastro."}
        {diaFixo && <> Depois, a cobrança é mensal, sempre no <b>dia {diaFixo}</b> de cada mês, a partir de <b>{dia(info.next_due)}</b>.</>}
      </div>

      <div className="rounded-xl border p-4 text-sm space-y-1.5" data-testid="payment-setup-summary">
        <div className="flex justify-between"><span className="text-muted-foreground">Plano</span><span className="font-medium">{info.plan_name}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Mensalidade</span><span className="font-medium">{brl(info.value)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">1ª cobrança</span><span className="font-medium">{dia(info.first_due)}</span></div>
        {diaFixo && (
          <div className="flex justify-between"><span className="text-muted-foreground">Próximas</span><span className="font-medium">todo dia {diaFixo}, a partir de {dia(info.next_due)}</span></div>
        )}
        <p className="text-xs text-muted-foreground pt-1">Acesso liberado assim que a forma de pagamento for cadastrada. Sem fidelidade e sem multa de cancelamento. Você pode pedir o encerramento a qualquer momento, com aviso prévio de 30 dias. Durante esse período, o serviço e as mensalidades continuam normalmente. Antes de confirmar, mostramos as datas de encerramento, as cobranças restantes e o prazo para exportar seus dados.</p>
      </div>

      <div className="space-y-2" role="radiogroup" aria-label="Forma de pagamento">
        {METHODS.map(({ key, label, hint, Icon }) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={method === key}
            onClick={() => setMethod(key)}
            className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
              method === key ? "border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/20" : "hover:bg-muted/40"
            }`}
            data-testid={`payment-method-${key.toLowerCase()}`}
          >
            <Icon className={`w-5 h-5 ${method === key ? "text-emerald-700" : "text-muted-foreground"}`} />
            <span className="flex-1">
              <span className="block text-sm font-medium">{label}</span>
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
          </button>
        ))}
      </div>

      {method === "CREDIT_CARD" && (
        <div className="space-y-3" data-testid="payment-setup-address">
          <p className="text-xs text-muted-foreground">Endereço da empresa (a operadora do cartão exige):</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <Label className="text-xs">CEP</Label>
              <Input value={addr.cep} inputMode="numeric" maxLength={9}
                onChange={(e) => setAddr((a) => ({ ...a, cep: e.target.value }))}
                onBlur={(e) => lookupCep(e.target.value)} data-testid="address-cep" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Rua</Label>
              <Input value={addr.logradouro} onChange={(e) => setAddr((a) => ({ ...a, logradouro: e.target.value }))} data-testid="address-street" />
            </div>
            <div>
              <Label className="text-xs">Número</Label>
              <Input value={addr.numero} onChange={(e) => setAddr((a) => ({ ...a, numero: e.target.value }))} data-testid="address-number" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Bairro</Label>
              <Input value={addr.bairro} onChange={(e) => setAddr((a) => ({ ...a, bairro: e.target.value }))} data-testid="address-district" />
            </div>
          </div>
          {addr.municipio && <p className="text-xs text-muted-foreground">{addr.municipio}{addr.uf ? `/${addr.uf}` : ""}</p>}
        </div>
      )}

      {needsTerms && (
        <label className="flex items-start gap-3 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
            className="mt-0.5 shrink-0" data-testid="payment-setup-terms" />
          <span>
            Li e aceito os{" "}
            <a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline text-emerald-700 font-medium">
              Termos de Uso e Contrato de Licença
            </a>{" "}
            da OPLYX, com os Anexos I (dados pessoais) e II (inteligência artificial).
          </span>
        </label>
      )}

      <Button onClick={submit} disabled={!canSubmit} className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="payment-setup-submit">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {method === "CREDIT_CARD" ? "Cadastrar cartão" : "Confirmar e liberar o acesso"}
      </Button>
      {method === "CREDIT_CARD" && (
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> O cartão é digitado na página segura do Asaas, nosso parceiro de pagamentos.
          </p>
          <p data-testid="payment-setup-card-note">
            Lá o botão aparece como "Pagar {brl(info.value)}": ele só cadastra o cartão. O débito acontece em {dia(info.first_due)}.
          </p>
        </div>
      )}
    </div>
  );
}
